
import styled, { keyframes } from 'styled-components';
import { Button, Input, Modal, Progress, Select } from 'antd';
import { DEFAULT_FONT } from '../../../../styles/variables';

const popIn = keyframes` 
from {
  transform: scale(0);
}
to {
  transform: scale(1);
}
`;

export const StyledModal = styled(Modal)`
  .ant-modal {
    max-width: 896px;
    width: calc(100% - 2rem);
    padding: 0;
  }

  .ant-modal-content {
    border-radius: 8px;
    padding: 0;
    background-color: white;
  }

  .ant-modal-header {
    padding: ${props => props.isHybrid ? '24px 34px':'32px 32px 24px'}!important;
    border-bottom: none;
    background: transparent;
  }

  .ant-modal-body {
    padding: 0 32px 32px;
  }

  .ant-modal-footer {
    display: none;
  }

  .ant-modal-close {
    display: none;
  }
`;

export const ModalTitle = styled.div`
  font-family: 'Oswald', sans-serif;
  font-size: 36px;
  text-align: center;
  color: #000;
  font-weight: 400;
  margin-bottom: 0;
  line-height: 1.2;
`;

export const ModalDescription = styled.div`
  text-align: center;
  color: #6B7280;
  font-size: 12px;
  line-height: 1.5;
  margin-top: 8px;
`;

export const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props=> props.isHybrid ? '10px': '24px'};

  .ant-input {
    border-radius: 12px;
    padding-block: 15px;
    &:hover {
      border:  1px solid #e5e7eb !important;
    }
    &:focus {
      border: 2px solid ${props => props.isStepTwo ? '#0092b8' : '#927bf8'} !important;
      box-shadow: none;
    }
  }
`;

export const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
`;

export const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.isHybrid ? '0px' : '8px'};
`;

export const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
`;

export const Required = styled.span`
  color: #ef4444;
`;

export const StyledSelect = styled(Select)`
  height: 36px;

  .ant-select-selection {
    border-radius: 12px !important;
    height: 40px !important;
    display: flex;
    align-items: center;
    box-shadow: none !important;
    &:hover {
      border:  1px solid #e5e7eb !important;
    }
    &:focus {
      border: 2px solid ${props => props.isStepTwo ? '#0092b8' : '#927bf8'} !important;
      box-shadow: none;
    }
  }

  .ant-select-selector {
    height: 36px !important;
    background-color: white !important;
    border: 1px solid #D1D5DB !important;
    border-radius: 6px !important;
    padding: 0 12px !important;
    display: flex;
    align-items: center;
  }

  .ant-select-selection-search {
    display: flex;
    align-items: center;
  }

  .ant-select-selection-item {
    line-height: 36px !important;
    padding: 0 !important;
  }

  &.ant-select-focused .ant-select-selector {
    border-color: #D1D5DB !important;
    box-shadow: none !important;
  }

  &:hover .ant-select-selector {
    border-color: #D1D5DB !important;
  }
`;

export const StyledInput = styled(Input)`
  height: 36px;
  background-color: white;
  border: 1px solid #D1D5DB;
  border-radius: 6px;
  padding: 0 12px;
  font-size: 14px;
  
  &:hover {
    border-color: #D1D5DB;
  }

  &:focus {
    border-color: #D1D5DB;
    box-shadow: none;
    outline: none;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background-color: white;
    color: rgba(0, 0, 0, 0.25);
  }

  &::placeholder {
    color: #9CA3AF;
  }
`;

export const DragDropZone = styled.div`
  position: relative;
  border: 2px dashed ${props => props.$isDragging ? '#3B82F6' : '#D1D5DB'};
  border-radius: 8px;
  background-color: ${props => props.$isDragging ? '#EFF6FF' : '#F9FAFB'};
  min-height: 300px;
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    background-color: #F3F4F6;
    border-color: #9CA3AF;
  }
`;

export const FileInput = styled.input`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
  z-index: 10;
`;

export const UploadContent = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px;
`;

export const UploadCenter = styled.div`
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
`;

export const IconWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

export const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const UploadTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  margin: 0;
  line-height: 1.75;
`;

export const UploadText = styled.p`
  font-size: 14px;
  color: #6B7280;
  margin: 0;
  line-height: 1.25;
`;

export const UploadHint = styled.p`
  font-size: 12px;
  color: #9CA3AF;
  margin: 8px 0 0 0;
  line-height: 1;
`;

export const FileDisplay = styled.div`
  position: absolute;
  bottom: 16px;
  left: 16px;
  right: 16px;
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  border: 1px solid #E5E7EB;
  z-index: 20;
`;

export const FileItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const FileHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

export const FileInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  flex: 1;
`;

export const FileIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: linear-gradient(135deg, #3B82F6 0%, #2563EB 100%);
  flex-shrink: 0;
`;

export const FileDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  flex: 1;
`;

export const FileName = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.25;
`;

export const FileSize = styled.span`
  font-size: 12px;
  color: #6B7280;
  line-height: 1;
`;

export const RemoveButton = styled.button`
  flex-shrink: 0;
  padding: 6px;
  background: transparent;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  z-index: 30;

  &:hover {
    background-color: #FEE2E2;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 16px;
`;

export const ProgressSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const ProgressHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

export const ProgressLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #6B7280;
`;

export const CloudIconWrapper = styled.div`
  display: flex;
  align-items: center;
  animation: ${props => props.$isUploading ? 'pulse 1.5s ease-in-out infinite' : 'none'};

  @keyframes pulse {
    0%, 100% {
      opacity: 1;
      transform: scale(1);
    }
    50% {
      opacity: 0.7;
      transform: scale(1.1);
    }
  }
`;

export const ProgressPercent = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${props => props.$complete ? '#10B981' : '#3B82F6'};
`;

export const StatusBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 12px;
  background-color: ${props => props.$complete ? '#D1FAE5' : '#DBEAFE'};
  font-size: 12px;
  font-weight: 500;
  color: ${props => props.$complete ? '#059669' : '#1D4ED8'};
`;

export const StyledProgress = styled(Progress)`
  .ant-progress-bg {
    background: linear-gradient(90deg, #3B82F6 0%, #2563EB 100%);
    border-radius: 4px;
  }

  .ant-progress-success-bg {
    background: linear-gradient(90deg, #10B981 0%, #059669 100%);
  }

  .ant-progress-inner {
    background-color: #E5E7EB;
    border-radius: 4px;
  }
`;



export const ProgressWrapper = styled.div`
  margin-top: 10px;
  width: 100%;
`;

export const ProgressBar = styled.div`
  height: 8px;
  background-color: #E5E7EB;
  border-radius: 4px;
  overflow: hidden;
`;

export const ProgressFill = styled.div`
  height: 100%;
  background-color: #3B82F6;
  width: ${props => props.percent || 0}%;
  transition: width 0.3s ease;
`;

export const CancelButton = styled(Button)`
  height: 36px;
  padding: 0 24px;
  font-size: 14px;
  border-radius: 6px;
  border: 1px solid #D1D5DB;
  
  &:hover {
    color: rgba(0, 0, 0, 0.88);
    border-color: #D1D5DB;
  }
`;

export const PrimaryButton = styled(Button)`
  height: 36px;
  padding: 0 24px;
  font-size: 14px;
  border-radius: 6px;
  // background-color: #2563EB;
  // border-color: #2563EB;

  // &:hover:not(:disabled) {
  //   background-color: #1D4ED8 !important;
  //   border-color: #1D4ED8 !important;
  // }

  // &:focus:not(:disabled) {
  //   background-color: #1D4ED8 !important;
  //   border-color: #1D4ED8 !important;
  // }

  // &:disabled {
  //   background-color: #93C5FD;
  //   border-color: #93C5FD;
  //   opacity: 0.6;
  // }
    background-color: #ffeb3b !important;
    border: #ffeb3b !important;
    color: #000 !important;
`;

// ----------------------


export const Container = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
  font-family:  ${DEFAULT_FONT}, sans-serif;
  font-weight: 500;
`;

export const Sidebar = styled.div`
  width: 26vw;
  background: rgb(37, 41, 45);
  color: white;
  padding: 24px;
  margin-left: 5%;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media only screen and (min-width: 2560px) {
    margin-left: 150px;
  }
`;

export const PDFPreview = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    width : 74vw;
    position: relative;
`;

export const PDFHeader = styled.div`
  display: flex;
  flex-direction: row;
  height: 10%;
  justify-content: flex-end;
  align-items: center;
  gap: 20px;
  padding: 0px 20px;
`

export const ReturnButton = styled.button`
  z-index: 100
  padding: 10px 24px;
  background-color: #000;
  border: 2px solid white;
  color: white;
  border-radius: 12px;
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &:hover {
    background-color: #111827;
  }
`;

export const PDFContainer = styled.div`
    display: flex;
    justify-content: center;
    overflow: auto;
    height: 100%;
    width: 100%;
`;

export const SidebarTitle = styled.h2`
  color: white;
  margin-bottom: 15px;
  font-size: 22px;
`;

export const PreviewSection = styled.div`
  margin-top: 0px;
  padding-top: 24px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: scroll;
  scrollbar-width: none;
`;

export const PreviewTitle = styled.h3`
  color: white;
  margin-bottom: 24px;
  font-size: 17px;
`;

export const DocumentListTitle = styled.div`
  display: flex;
  flex: 1;
  justify-content: space-between;
  color: white;
  font-size: 17px;
  padding-top: 24px;
  padding-bottom: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  text-transform: capitalize;
`;

export const ButtonRow = styled.div`
  display: flex;
  justify-content: space-between;
`;

export const RowSection = styled.div`
  margin-top: ${props => props.marginTop && '20px'};
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: ${props => props.isPreview && 'space-between'};
  .swith-btn{
    color:white;
    display: flex;
    align-items: center;
  }
  .ant-btn,
  .ant-btn:hover,
  .ant-btn:focus,
  .ant-btn:active {
    outline: 0;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 1px;
    text-transform: uppercase;
    border-radius: 50px;
    background-color: ${props => props.releaseBtn ? '#6969d8' : '#ffeb3b'} ;
    border: #ffeb3b;
    color: ${props => props.releaseBtn ? 'white' : '#000'};
    min-width: 150px;
  }
  .ant-btn .anticon {
    font-size: 13px;
  }
  .ant-btn.preview {
    display: flex;
    align-items: center;
    justify-content: center;
    .anticon {
      margin-right: 10px !important;
    }
    p {
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 1px;
      text-transform: uppercase;
      color: #000;
    }
  }
  .error-message{
    color: #ff0000;
  }
  ${props => props.downloadPayload && `
    justify-content: flex-end;
    .ant-btn, .ant-btn:hover, .ant-btn:focus {
      background: transparent;
      color: #FF9D00;
    }
  `}
`;

export const DownloadButton = styled.div`
    outline: 0;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 1px;
    text-transform: uppercase;
    border-radius: 50px;
    background-color: #ffeb3b;
    border: #ffeb3b;
    color: #000;
    width: ${props => props.channel && '50%'};
    cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
    height: 32px;
    padding: 0 15px;
    display: flex;
    justify-content: center;
    align-items: center;
    pointer-events: ${props => props.disabled ? 'none' : 'all'};
    
    .ant-dropdown-menu {
      width: 300px;
      }
      .ant-dropdown-menu-item, .ant-dropdown-menu-submenu-title {
        font-weight: bold;
        font-size: 10px;
        padding: 6px 12px;
        margin: 1px 0px;
        text-wrap: wrap;
        
        &:hover {
          background: #8C52FF !important;
          color: white;
          }
          }
          .ant-dropdown-menu-item-group-title {
            font-size: 9px;
            }
            
            &.transaction-btn {
              margin: 0 10px;
              min-width: 190px;
              background-color: #6f55ff;
              border: #6f55ff;
              color: #ffffff;
              cursor: ${props => props.interactiveEditable ? 'pointer' : 'not-allowed'};
    }
    &.loading {
        width: 230px;
    }
    ${props => props.disabled && `
      opacity: 0.5;
    `};
`;

export const WindowGuide = styled.div`
  position: absolute;
  left: ${p => p.x}px;
  top: ${p => p.y}px;
  width: ${p => p.width}px;
  height: ${p => p.height}px;
  border: 2px dashed rgba(0, 0, 0, 0.3);
  pointer-events: none;
  z-index: 5;
`;

export const AddressOverlay = styled.div`
  position: absolute;
  left: ${p => p.x}px;
  top: ${p => p.y}px;
  background: rgba(255, 255, 255, 0.95);
  border: 2px solid ${p => (p.valid ? '#22c55e' : '#ef4444')};
  border-radius: 4px;
  cursor: move;
  z-index: 10;
  will-change: transform;
`;

export const AddressTextarea = styled.textarea`
  border: none;
  resize: none;
  outline: none;
  background: transparent;
  scrollbar-width: none;
  font-family: ${({ fontStyle }) =>
    fontStyle?.fontName
      ? `${fontStyle.fontName}, sans-serif !important`
      :  'sans-serif'};
  font-size: ${({ fontStyle, scale }) =>
    fontStyle?.fontSize ? `${fontStyle.fontSize * scale}px` : '12pt'};
  line-height: ${({ fontStyle }) =>
    fontStyle?.lineHeight ? `${fontStyle?.lineHeight}em` : '1.2em'};
  color: ${({ fontStyle }) =>
    fontStyle?.fontColor ?? '#000'};
  font-weight: ${({ fontStyle }) =>
    fontStyle?.bold ? '700' : '400'};
  font-style: ${({ fontStyle }) =>
    fontStyle?.italic ? 'italic' : 'normal'};
  text-decoration: ${({ fontStyle }) =>
    fontStyle?.underline ? 'underline' : 'none'};
`;

export const DocumentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 16px 0 10px 0;
  // overflow-y: auto;
  flex: 1;
  // padding-right: 8px;
  scrollbar-width: none;
`;

export const DocumentItem = styled.div`
  background: ${props => (props.selected ? '#E6F0FF' : 'white')};
  border: 2px solid ${props => (props.selected ? '#1890ff' : 'transparent')};
  border-radius: 8px;
  padding: 12px;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
  pointer-events : ${props => (props.disabled ? 'none': 'auto')};

  &:hover {
    background: ${props => (props.selected ? '#E6F0FF' : '#f5f5f5')};
  }
`;

export const DocumentHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 4px;
  
  .anticon {
    color: #666;
    margin-top: 4px;
  }
`;

export const DocumentTitle = styled.div`
  font-weight: 600;
  color: #333;
  font-size: 14px;
`;

export const DocumentAddress = styled.div`
  color: #666;
  font-size: 12px;
  margin-left: 22px;
  // margin-bottom: 8px;
  line-height: 1.4;
`;

export const DocumentMeta = styled.div`
  display: flex;
  justify-content: end;
  align-items: center;
  margin-left: 22px;
  color: #888;
  font-size: 11px;
`;

export const NavigationButton = styled.div`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 40px;
  height: 60px;
  background: rgba(0, 0, 0, 0.05);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  z-index: 100;
  border-radius: 4px;

  ${props => props.left ? 'left: 20px;' : 'right: 20px;'}

  &:hover {
    background: rgba(0, 0, 0, 0.1);
  }

  ${props => props.disabled && `
    opacity: 0;
    pointer-events: none;
  `}
`;

export const ExitButton = styled.div`
  z-index: 100;
  background-color: #000;
  border: 2px solid white;
  color: white;
  border-radius: 12px;
  padding: 10px 24px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s;

  &:hover {
    background: rgba(0, 0, 0, 0.8);
  }
`;

export const LoaderContainer = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translateX(-50%);
  z-index: 12;
  background: rgba(255, 255, 255, 0.8);
  padding: 8px 16px;
  border-radius: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
`;

export const InsertContainer = styled.div`
  display: flex;
  width: 50%;
`;

export const ShiftContainer = styled.div`
  display: flex;
  width: 50%;

  .ant-select.shiftContent {
    // min-width: calc(100% - 100px);
    // max-width: calc(100% - 100px);
    width: 100%;
    z-index:1;
    .ant-select-selection--single .ant-select-selection__rendered {
      margin-right: 11px;
    }
  }
  
  .ant-select.shiftUnits {
    min-width: 42%;
    margin-left: -5px;
    .ant-select-selection--single .ant-select-selection__rendered {
      margin-right: 11px;
    }
    // .ant-select-arrow {
    //   display:none;
    // }
  }
  
  
`;

export const Prefix = styled.div`
  width: 40px;
  height: 32px;
  background: #e7e7e7;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s;
  border-top-left-radius: 4px;
  border-bottom-left-radius: 4px;
  margin-right: -3px;

  .ant-checkbox-disabled.ant-checkbox-checked .ant-checkbox-inner::after { 
    border-color: #fff;
  }
`;

export const UploadButton = styled.div`
  width: 40px;
  height: 32px;
  background: #ffeb3b;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s;
  border-top-right-radius: 4px;
  border-bottom-right-radius: 4px;
  margin-left: -3px;
  pointer-events: ${({disabled}) => disabled ? 'none' : 'auto'}
  opacity:${({disabled}) => disabled ? 0.5 : 1}
  &:hover {
    background: #ffe066;
  }

  .anticon {
    color: black;
    font-size: 16px;
    font-weight: bold;
  }
`;

export const UpdateAddressButton = styled.div`
  z-index: 100;
  background: rgba(255, 234, 59, 0.8);
  color: #000;
  padding: 10px 24px;
  border-radius: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s;

  &:hover {
    background: rgba(255, 234, 59, 1);
  }

  .anticon {
    font-size: 16px;
  }
`;

export const PaginationContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 16px 0 0 0;
  width: 100%;
`;

export const PageNavButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 30px;
  background: white;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  color: #000;

  &:hover:not(:disabled) {
    background: #F9FAFB;
    border-color: #D1D5DB;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .anticon {
    font-size: 14px;
  }
`;

export const PageDisplay = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #fff;
  font-size: 15px;
  font-weight: 500;
  .ant-input {
    width:35% !important;
    height: 30px !important;
    text-align:center;
  }
`;

export const PageText = styled.span`
  color: #fff;
  font-size: 15px;
`;

export const DocListOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 5%;
  height: 100vh;
  width: 95%;
  background-color: #ffffff;
  z-index: 101;
  animation: ${popIn} ease 300ms;
  display: flex;
  flex-direction: column;

  .hybrid-queue-table{
    font-family: 'Lato' !important;
  }
`;

export const DocListFooter = styled.div`
  pointer-events: ${(props) => props.isLoading ? "none" : "all"};
  opacity: ${(props) => props.isLoading ? ".5" : "1"};
  display: flex;
  height: ${props => props.height};
  width: ${props => props.width};
  padding: 0 90px;
  justify-content: flex-end;
  align-items: center;
  div {
    width: 100%;
    display: flex;
    justify-content: space-between;
  }
  .ant-btn-primary,
  .ant-btn {
    width: 160px;
    color: #ffffff;
    background-color: #66b5ff;
    border: 0;
    height: 38px;
    text-transform: uppercase;
    font-family: ${DEFAULT_FONT};
    font-weight: 600;
    font-size: 12px !important;
    letter-spacing: 2px;
    border-radius: 50px;
    transition: all 0.4s ease-in-out;
  }
  .ant-btn-primary:hover,
  .ant-btn:hover {
    transform: scale(104%);
  }
  .customButton {
    width: 180px;
    font-size: 14px;
    font-family: ${DEFAULT_FONT} !important;
    text-transform: uppercase;
    font-weight: 700;
    color: #ffffff;
    letter-spacing: 1px;
    cursor: pointer;
    height: 38px;
    color: #ffffff;
    cursor: pointer;
    text-align: center;
    border: none;
    background-size: 300% 100%;
    border-radius: 50px;
    moz-transition: all 0.4s ease-in-out;
    -o-transition: all 0.4s ease-in-out;
    -webkit-transition: all 0.4s ease-in-out;
    transition: all 0.4s ease-in-out;
    background-image: linear-gradient(to right, #25aae1, #4481eb, #04befe, #3f86ed);
  }
  .deleteButton {
    width: 240px;
    background-image: linear-gradient(to right, #eb3941, #f15e64, #e14e53, #e2373f);
    box-shadow: 0 5px 15px rgba(242, 97, 103, 0.4);
  }
  .customButton:hover {
    background-position: 100% 0;
    moz-transition: all 0.4s ease-in-out;
    -o-transition: all 0.4s ease-in-out;
    -webkit-transition: all 0.4s ease-in-out;
    transition: all 0.4s ease-in-out;
    transform: scale(104%);
  }
  .customButton:focus {
    outline: none;
  }
`;

export const ContentContainer = styled.div`
  height: ${props => props.height};
  display: flex;
  overflow: scroll;
`;

export const CloseIcon = styled.div`
  color: #000000;
  .anticon svg:hover {
    color: #ff3333;
    cursor: pointer;
    transform: scale(125%);
    transition: 200ms ease transform;
  }
`;

export const PageFilter = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  column-gap: 12px;
  .page-filter-title {
    display: flex;
    flex-direction: column;
    row-gap: 6px;
    p {
      font-size: 12px;
    }
    .ant-input-number-input {
      font-family: inherit !important;
      font-size: 14px;
      font-weight: 600;
    }
  }
`;

export const DateRangeFilter = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  column-gap: 12px;
  padding: 5px;
  background-color: #fbfbfb;
  border-radius: 12px;
  .ant-select {
    font-size: 10px;
    margin-left: 12px;
    @media only screen and (min-width: 1640px) {
      font-size: 14px;
    }
  }
  .ant-select-selection--single {
    border-radius: 30px;
    background: #f1f0f6;
    @media only screen and (min-width: 1640px) {
      height: 36px;
      padding: 2px 0;
    }
  }
  .ant-select-selection--multiple .ant-select-selection__rendered {
    font-family: ${DEFAULT_FONT} !important;
  }

  .ant-input {
    cursor: pointer;
    border-radius: 30px;
    background-color: #ffffff;
    @media only screen and (min-width: 1640px) {
      height: 36px;
    }
  }
  .ant-select-selection--single .ant-select-selection__rendered {
    color: #000000;
  }
  .dateTimePicker {
    width: 220px;
    label {
      font-size: 0.7rem;
    }
    input[type='text'] {
      font-size: 0.7rem;
      font-family: ${DEFAULT_FONT} !important;
      cursor: pointer;
    }
  }
`;

export const Filters = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 12px;
  align-items: flex-end;
  .searchDoc {
    display: flex;
    justify-content: flex-end;
    column-gap: 16px;
    .ant-input-suffix {
      top: 80%;
    }
  }
  .tagFilter {
    z-index: 3;
    display: flex;
    align-items: center;
    justify-content: space-around;
    border: 1px solid #d9d9d9;
    width: 110px;
    padding: 0 10px;
    height: 32px;
    border-radius: 5px;
    font-weight: 600;
    cursor: pointer;
    position: relative;
    column-gap: 4px;
    p {
      font-size: 0.7rem;
      color: rgb(143 141 141);
      font-weight: 600;
      letter-spacing: 0.5px;
    }
    .tagsCount {
      background: #919191;
      border-radius: 50%;
      position: absolute;
      right: -10%;
      bottom: 58%;
      height: 20px;
      width: 20px;
      color: #ffffff !important;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 10px !important;
    }
    .anticon {
      font-size: 15px;
    }
    .ant-select {
      position: absolute;
      top: 105%;
      right: 0%;
      width: 475px !important;
      z-index: 1000 !important;
    }

    .ant-select-selection--multiple {
      max-height: 104px !important;
      overflow-y: scroll !important;
      border-radius: 16px;
      font-size: 0.8rem !important;
      font-weight: 500 !important;
    }
    .ant-select-selection--multiple .ant-select-selection__rendered > ul > li {
      height: 30px;
      line-height: 30px !important;
    }
    .ant-select-selection__choice {
      font-size: 0.7rem !important;
      background: #ffdd80;
      border: 0;
      height: 32px;
      border-radius: 15px;
      line-height: unset !important;
    }
    .anticon-close.ant-select-remove-icon {
      font-size: 10px !important;
    }
  }
  .ant-input-search.ant-input-affix-wrapper {
    height: 30%;
    width: 350px !important;
  }
  .ant-input-affix-wrapper .ant-input-suffix {
    transform: none;
  }
  .ant-input-affix-wrapper .ant-input {
    font-size: 12px;
    font-family: ${DEFAULT_FONT} !important;
    border-radius: 18px;
  }
  .anticon svg {
    color: #000000;
  }
  .ant-select-selection--single .ant-select-selection__rendered {
    color: #000000;
  }

  .ant-radio-button-wrapper {
    font-size: 12px;
    line-height: 26px;
  }
  .ant-radio-button-wrapper:first-child {
    border-radius: 12px 0 0 12px;
  }
  .ant-radio-button-wrapper:last-child {
    border-radius: 0 12px 12px 0;
  }
  .ant-checkbox + span {
    font-size: 12px !important;
    text-transform: uppercase;
    font-weight: 500;
    letter-spacing: 0.25px;
  }
  .selectAll {
    display: flex;
    align-items: center;
    p {
      font-size: 12px !important;
      text-transform: uppercase;
      font-weight: 500;
      letter-spacing: 0.25px;
      padding-left: 8px;
      padding-right: 8px;
      line-height: 2.2 !important;
    }
  }
`;